import { FORM_VALIDATOR_TYPES, SCREEN_MODE } from '../constants';
import { IMyInstitute } from '../interface';
import { IInstituteFormData } from '../interface/instituteModel';

export enum LEARNING_SETTING {
  ONLINE = 'ONLINE',
  PHYSICAL = 'PHYSICAL',
  HYBRID = 'HYBRID',
}

/**
 * Helper function to filter out "unknown" string values
 * Returns null if value is "unknown", undefined, null, or empty string
 */
const sanitizeValue = (value: any): any => {
  if (
    value === 'unknown' ||
    value === 'Unknown' ||
    value === '' ||
    value === null ||
    value === undefined
  ) {
    return null;
  }
  return value;
};

export function mapOrganizationToFormData(
  org: IMyInstitute,
  mode: SCREEN_MODE,
): IInstituteFormData {
  const isView = mode === SCREEN_MODE.VIEW;

  return {
    ...INSTITUTE_FORM_INIT,
    id: {
      ...INSTITUTE_FORM_INIT.id,
      value: org.id || '',
      disable: isView,
    },
    name: {
      ...INSTITUTE_FORM_INIT.name,
      value: org.name || '',
    },
    contactNo: {
      ...INSTITUTE_FORM_INIT.contactNo,
      value: org.contactNo || '',
      disable: isView,
    },
    email: {
      ...INSTITUTE_FORM_INIT.email,
      value: org.email || '',
      disable: isView,
    },
    website: {
      ...INSTITUTE_FORM_INIT.website,
      value: org.website || '',
      disable: isView,
    },
    fax: {
      ...INSTITUTE_FORM_INIT.fax,
      value: org.fax || null,
      disable: isView,
    },
    teacherCount: {
      ...INSTITUTE_FORM_INIT.teacherCount,
      value: org.teacherCount?.toString() || '',
    },
    noOfStudent: {
      ...INSTITUTE_FORM_INIT.noOfStudent,
      value: org.numberOfStudents || org.noOfStudent || null,
      disable: true, // Always disabled
    },
    schoolType: {
      ...INSTITUTE_FORM_INIT.schoolType,
      value: org.schoolTypeId || '',
      disable: true, // Always disabled
    },
    logo: {
      ...INSTITUTE_FORM_INIT.logo,
      value: org.logoUrl || '',
      disable: isView,
    },
    secondaryLogo: {
      ...INSTITUTE_FORM_INIT.secondaryLogo,
      value: org.secondaryLogoUrl || '',
      disable: isView,
    },
    businessRegistrationNo: {
      ...INSTITUTE_FORM_INIT.businessRegistrationNo,
      value: org.businessRegistrationNo,
      disable: isView,
    },
    physicalAddressId: {
      ...INSTITUTE_FORM_INIT.physicalAddressId,
      value: org.physicalAddress?.addressId,
      disable: isView,
    },
    physicalAddressType: {
      ...INSTITUTE_FORM_INIT.physicalAddressType,
      value: org.physicalAddress?.type || '',
      disable: isView,
    },
    physicalAddressLine1: {
      ...INSTITUTE_FORM_INIT.physicalAddressLine1,
      value: org.physicalAddress?.addressLine1 || null,
      disable: isView,
    },
    physicalAddressLine2: {
      ...INSTITUTE_FORM_INIT.physicalAddressLine2,
      value: org.physicalAddress?.addressLine2 || null,
      disable: isView,
    },
    physicalCity: {
      ...INSTITUTE_FORM_INIT.physicalCity,
      value: sanitizeValue(org.physicalAddress?.city),
      disable: isView,
    },
    physicalPostalCode: {
      ...INSTITUTE_FORM_INIT.physicalPostalCode,
      value: org.physicalAddress?.postalCode || null,
      disable: isView,
    },
    physicalCountry: {
      ...INSTITUTE_FORM_INIT.physicalCountry,
      value: sanitizeValue(org.physicalAddress?.country),
      disable: isView,
    },
    physicalMapLink: {
      ...INSTITUTE_FORM_INIT.physicalMapLink,
      disable: isView,
    },
    physicalLatitude: {
      ...INSTITUTE_FORM_INIT.physicalLatitude,
      value: org.physicalAddress?.latitude || null,
      disable: isView,
    },
    physicalLongitude: {
      ...INSTITUTE_FORM_INIT.physicalLongitude,
      value: org.physicalAddress?.longitude || null,
      disable: isView,
    },
    mailingAddressId: {
      ...INSTITUTE_FORM_INIT.mailingAddressId,
      value: org.mailingAddress?.addressId || '',
      disable: isView,
    },
    mailingAddressType: {
      ...INSTITUTE_FORM_INIT.mailingAddressType,
      value: org.mailingAddress?.type || '',
      disable: isView,
    },
    mailingAddressLine1: {
      ...INSTITUTE_FORM_INIT.mailingAddressLine1,
      value: org.mailingAddress?.addressLine1 || null,
      disable: isView || org.isSameAddress,
    },
    mailingAddressLine2: {
      ...INSTITUTE_FORM_INIT.mailingAddressLine2,
      value: org.mailingAddress?.addressLine2 || null,
      disable: isView || org.isSameAddress,
    },
    mailingCity: {
      ...INSTITUTE_FORM_INIT.mailingCity,
      value: sanitizeValue(org.mailingAddress?.city),
      disable: isView || org.isSameAddress,
    },
    mailingPostalCode: {
      ...INSTITUTE_FORM_INIT.mailingPostalCode,
      value: org.mailingAddress?.postalCode || null,
      disable: isView || org.isSameAddress,
    },
    mailingCountry: {
      ...INSTITUTE_FORM_INIT.mailingCountry,
      value: sanitizeValue(org.mailingAddress?.country),
      disable: isView || org.isSameAddress,
    },
    mailingMapLink: {
      ...INSTITUTE_FORM_INIT.mailingMapLink,
      disable: isView || org.isSameAddress,
    },
    mailingLatitude: {
      ...INSTITUTE_FORM_INIT.mailingLatitude,
      value: org.mailingAddress?.latitude || null,
      disable: isView || org.isSameAddress,
    },
    mailingLongitude: {
      ...INSTITUTE_FORM_INIT.mailingLongitude,
      value: org.mailingAddress?.longitude || null,
      disable: isView || org.isSameAddress,
    },
    isSameAddress: {
      ...INSTITUTE_FORM_INIT.isSameAddress,
      value: org.isSameAddress || false,
      disable: isView,
    },
    currency: {
      ...INSTITUTE_FORM_INIT.currency,
      value: org.currency || '',
      disable: true, // Always disabled
    },
    timezone: {
      ...INSTITUTE_FORM_INIT.timezone,
      value: org.timezone || '',
      disable: true, // Always disabled
    },
    learningSetting: {
      ...INSTITUTE_FORM_INIT.learningSetting,
      value: org.learningSetting || '',
      disable: true, // Always disabled
    },
  };
}

export const INSTITUTE_FORM_INIT: IInstituteFormData = {
  id: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  name: {
    value: '',
    isRequired: false,
    error: null,
    disable: true,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  contactNo: {
    value: '',
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  email: {
    value: '',
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.EMAIL,
  },
  website: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  fax: {
    value: null,
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  noOfStudent: {
    value: 0,
    isRequired: false,
    error: null,
    disable: true,
    validator: FORM_VALIDATOR_TYPES.NUMBER,
  },
  schoolType: {
    value: '',
    isRequired: false,
    error: null,
    disable: true,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  teacherCount: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  logo: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  secondaryLogo: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  businessRegistrationNo: {
    value: null,
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    maxLength: 150,
  },
  physicalAddressId: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  physicalAddressType: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  physicalAddressLine1: {
    value: '',
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  physicalAddressLine2: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  physicalCity: {
    value: null,
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  physicalPostalCode: {
    value: '',
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  physicalCountry: {
    value: null,
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  physicalMapLink: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.GOOGLE_LINK,
  },
  physicalLatitude: {
    value: null,
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.NUMBER,
  },
  physicalLongitude: {
    value: null,
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.NUMBER,
  },
  mailingAddressId: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  mailingAddressType: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  mailingAddressLine1: {
    value: '',
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  mailingAddressLine2: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  mailingCity: {
    value: null,
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  mailingPostalCode: {
    value: '',
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  mailingCountry: {
    value: null,
    isRequired: true,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  mailingMapLink: {
    value: '',
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.GOOGLE_LINK,
  },
  mailingLatitude: {
    value: null,
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.NUMBER,
  },
  mailingLongitude: {
    value: null,
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.NUMBER,
  },
  isSameAddress: {
    value: false,
    isRequired: false,
    error: null,
    disable: false,
    validator: FORM_VALIDATOR_TYPES.BOOLEAN,
  },
  currency: {
    value: '',
    isRequired: false,
    error: null,
    disable: true,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  timezone: {
    value: '',
    isRequired: false,
    error: null,
    disable: true,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
  learningSetting: {
    value: '',
    isRequired: false,
    error: null,
    disable: true,
    validator: FORM_VALIDATOR_TYPES.TEXT,
  },
};
